import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Trophy, Lightning, ArrowRight, ClipboardText, CheckCircle, RocketLaunch } from "@phosphor-icons/react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const handleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Login() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [demoLoading, setDemoLoading] = useState(false);

  const tryDemo = async () => {
    setDemoLoading(true);
    try {
      await api.post("/auth/demo");
      await checkAuth();
      toast.success("Demo mode active — explore any feature freely");
      navigate("/dashboard");
    } catch (_) {
      toast.error("Could not start demo mode");
      setDemoLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-slate-950">
      {/* Background image with overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-25"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1681813336396-25cae24e1c37?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxydW5uaW5nJTIwdHJhY2slMjBkYXJrfGVufDB8fHx8MTc4NDIzNzU1Mnww&ixlib=rb-4.1.0&q=85')",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/50 via-slate-950/90 to-slate-950" />
      <div className="absolute -top-40 -left-40 w-96 h-96 bg-orange-500/15 rounded-full blur-3xl" />
      <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] bg-orange-500/10 rounded-full blur-3xl" />

      <div className="relative z-10 min-h-screen grid lg:grid-cols-2">
        {/* Left: Brand */}
        <div className="hidden lg:flex flex-col justify-between p-12 border-r border-white/10">
          <div className="flex items-center gap-3" data-testid="brand-mark">
            <div className="w-11 h-11 rounded-xl bg-orange-500 grid place-items-center shadow-lg shadow-orange-500/30">
              <ShieldCheck size={24} weight="bold" color="#fff7ed" />
            </div>
            <div>
              <div className="text-lg font-display font-black tracking-tight text-slate-50">AuditReady <span className="text-orange-400">Sports</span></div>
              <div className="font-label text-orange-400 mt-0.5">COMPLIANCE · SAFEGUARDING · AUDIT-READY</div>
            </div>
          </div>

          <div className="space-y-6 max-w-xl">
            <div className="font-label text-orange-400">The audit-readiness platform for sports</div>
            <h1 className="text-5xl xl:text-6xl font-display font-black tracking-tight leading-[1.03] text-slate-50">
              Every certificate on file. <span className="text-orange-400">Every audit answered.</span>
            </h1>
            <p className="text-slate-400 text-lg leading-relaxed">
              Youth leagues, high school athletic departments and club sports lose an audit — and sometimes their insurance —
              when a coach's SafeSport, a trainer's BOC or a referee's background check quietly lapses.
              AuditReady Sports keeps every coach, trainer, referee and volunteer credential tracked in one place,
              alerts you at 90 / 60 / 30 days and produces board-ready PDF and CSV reports on demand.
            </p>

            <div className="grid grid-cols-3 gap-3 pt-4">
              {[
                { icon: ShieldCheck, label: "Safeguarding & Background Checks", n: "Tracked" },
                { icon: Trophy, label: "Coach, Trainer & Referee Credentials", n: "24/7" },
                { icon: Lightning, label: "Alerts at 90/60/30 Days", n: "Auto" },
              ].map((f, i) => (
                <div key={i} className="rounded-xl border border-white/10 bg-slate-900/60 backdrop-blur-sm p-4">
                  <f.icon size={20} className="text-orange-400 mb-2" weight="bold" />
                  <div className="font-display font-black text-slate-50 text-lg">{f.n}</div>
                  <div className="text-xs text-slate-400 mt-1 leading-snug">{f.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-slate-500 text-xs font-label">
            Built for youth leagues · high schools · club programs
          </div>
        </div>

        {/* Right: Login card */}
        <div className="flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-md">
            <div className="lg:hidden flex items-center gap-3 mb-10">
              <div className="w-10 h-10 rounded-xl bg-orange-500 grid place-items-center">
                <ShieldCheck size={22} weight="bold" color="#fff7ed" />
              </div>
              <div>
                <div className="text-lg font-display font-black">AuditReady <span className="text-orange-400">Sports</span></div>
                <div className="font-label text-orange-400">COMPLIANCE · AUDIT-READY</div>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-slate-900/80 backdrop-blur-xl p-8 shadow-2xl">
              <div className="font-label text-orange-400 mb-3">SIGN IN</div>
              <h2 className="text-3xl font-display font-black tracking-tight mb-3">Ready when the audit calls.</h2>
              <p className="text-slate-400 mb-8">
                Continue with Google to access your organization's compliance workspace.
              </p>

              <button
                data-testid="google-login-button"
                onClick={handleLogin}
                className="google-btn w-full flex items-center justify-center gap-3 rounded-full py-3.5 px-6 text-base"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#fff7ed"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#fff7ed"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#fff7ed"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" fill="#fff7ed"/>
                </svg>
                Continue with Google
                <ArrowRight size={16} weight="bold" />
              </button>

              <div className="flex items-center gap-3 my-5">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-[10px] tracking-widest text-slate-500 font-semibold uppercase">Judging today?</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>

              <button
                data-testid="try-demo-button"
                onClick={tryDemo}
                disabled={demoLoading}
                className="w-full flex items-center justify-center gap-2 rounded-full py-3 px-6 text-sm font-semibold border border-orange-500/40 bg-orange-500/5 text-orange-200 hover:bg-orange-500/10 transition-colors"
              >
                <RocketLaunch size={16} weight="bold" />
                {demoLoading ? "Starting demo…" : "Try the demo — no signup"}
              </button>
              <p className="text-[11px] text-slate-500 text-center mt-2">
                Loads a fresh sample organization with realistic staff, credentials, alerts and reports. Session lasts 24 hours; nothing you do here touches a real organization.
              </p>

              <div className="mt-8 pt-6 border-t border-white/10">
                <div className="font-label text-slate-500 mb-3">WHAT'S INSIDE</div>
                <ul className="space-y-2.5 text-sm text-slate-400">
                  <li className="flex gap-2 items-start"><CheckCircle size={14} weight="bold" className="text-orange-400 mt-0.5 shrink-0" /> Live compliance dashboard and staff roster</li>
                  <li className="flex gap-2 items-start"><CheckCircle size={14} weight="bold" className="text-orange-400 mt-0.5 shrink-0" /> Automatic 90 / 60 / 30-day expiration alerts</li>
                  <li className="flex gap-2 items-start"><CheckCircle size={14} weight="bold" className="text-orange-400 mt-0.5 shrink-0" /> One-click audit exports as PDF and CSV</li>
                  <li className="flex gap-2 items-start"><CheckCircle size={14} weight="bold" className="text-orange-400 mt-0.5 shrink-0" /> Secure credential document upload</li>
                </ul>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="mt-6 rounded-xl border border-white/10 bg-slate-900/60 backdrop-blur-sm p-4">
              <div className="flex gap-2 items-start">
                <ClipboardText size={14} weight="bold" className="text-slate-500 mt-0.5 shrink-0" />
                <p className="text-[11px] leading-relaxed text-slate-500" data-testid="legal-disclaimer">
                  <span className="font-semibold text-slate-400">Independent tool.</span> AuditReady Sports is not affiliated with, endorsed by, or sponsored
                  by FIFA, U.S. Soccer, the U.S. Center for SafeSport, NFHS, the Board of Certification (BOC),
                  or any governing sports body. Credential names shown are for organizational reference only.
                  We do not verify credentials with issuing bodies — you remain responsible for the accuracy
                  of records entered.
                </p>
              </div>
            </div>

            <p className="text-center text-slate-600 text-xs mt-5">
              By continuing you agree to our terms of service.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
